<template>
  <div v-if="headings.length > 0" class="bg-[#f9f9f9] border border-[#e0e0e0] p-3 rounded-lg shadow-md">
    <OButton pure class="flex items-center gap-2 text-lg sm:text-20 font-semibold" @click="toggleTOC">
      <OIcon
        :icon="isTOCVisible ? 'chevron-right' : 'chevron-left'"
        svg-class=" h-5 w-5 p-1 text-primary-300 u-center rounded -rotate-90"
      />
      İçindekiler
    </OButton>

    <div v-if="isTOCVisible" class="toc max-h-[300px] overflow-y-auto mt-2.5">
      <ul class="space-y-2">
        <li v-for="(h2Item, index) in headings" :key="`h2-${index}`" class="text-gray-700">
          <!-- main title -->
          <a
            :href="`#${h2Item.id}`"
            class="block hover:text-blue-600 transition-colors duration-200 line-clamp-2 text-base sm:text-lg"
            @click.prevent="scrollTo($event.target.getAttribute('href').substring(1))"
          >
            {{ h2Item.text }}
          </a>

          <!-- sub title -->
          <ul v-if="h2Item.children && h2Item.children.length" class="pl-4 mt-2 space-y-1">
            <li v-for="(h3Item, h3Index) in h2Item.children" :key="`h3-${index}-${h3Index}`">
              <a
                :href="`#${h3Item.id}`"
                class="block text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 line-clamp-2"
                @click.prevent="scrollTo($event.target.getAttribute('href').substring(1))"
              >
                {{ h3Item.text }}
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, nextTick, onBeforeUnmount } from '@nuxtjs/composition-api';
import debounce from 'lodash/debounce';
import { useDevice } from '@/composables/useDevice';
import { OButton, OIcon } from '~/modules/common/components/ui';
import { normalizeText } from '~/modules/common/utils/stringUtils';

export default defineComponent({
  components: {
    OButton,
    OIcon,
  },
  props: {
    categoryName: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup() {
    const tocItems = ref([]);
    const headings = ref([]);
    const { isMobile } = useDevice();
    const isTOCVisible = ref(!isMobile.value);

    const toggleTOC = () => {
      isTOCVisible.value = !isTOCVisible.value;
    };

    // extract headings
    const extractHeadings = () => {
      const articleElement = document.querySelector('article');
      if (!articleElement) return;

      const headingElements = articleElement.querySelectorAll('.entry h2, .entry h3');
      const hierarchy = [];
      let currentH2Item = null;

      Array.from(headingElements).forEach((heading) => {
        if (!heading.id) {
          const id = normalizeText(heading.textContent);
          heading.id = id;
        }

        const item = {
          element: heading,
          text: heading.textContent.trim(),
          id: heading.id,
          tag: heading.tagName.toLowerCase(),
        };

        if (heading.tagName === 'H2') {
          currentH2Item = {
            ...item,
            children: [],
          };
          hierarchy.push(currentH2Item);
        } else if (heading.tagName === 'H3' && currentH2Item) {
          currentH2Item.children.push(item);
        } else if (heading.tagName === 'H3') {
          hierarchy.push(item);
        }
      });

      headings.value = hierarchy;
    };

    // scroll to element
    const scrollTo = (id) => {
      const element = document.getElementById(id);
      if (element) {
        const headerHeight = 100;
        setTimeout(() => {
          const position = element.getBoundingClientRect().top + window.scrollY - headerHeight;
          window.scrollTo({
            behavior: 'smooth',
            top: position,
          });
        }, 50);
      }
    };

    // install MutationObserver for DOM changes
    const setupMutationObserver = () => {
      const articleElement = document.querySelector('article');
      if (!articleElement) return;

      const observer = new MutationObserver(
        debounce(() => {
          extractHeadings();
        }, 300)
      );

      observer.observe(articleElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id'],
      });

      // clean up observer when component is destroyed
      return () => observer.disconnect();
    };

    onMounted(() => {
      nextTick(() => {
        setTimeout(() => {
          extractHeadings();
          setupMutationObserver();
        }, 30);
      });
    });

    onBeforeUnmount(() => {
      const cleanup = setupMutationObserver();
      cleanup();
    });

    return {
      tocItems,
      isTOCVisible,
      toggleTOC,
      headings,
      scrollTo,
    };
  },
});
</script>
